import type { TEventrIcons } from "@eventr/common-storybook";
import { useNativeNavigationViewContext } from "@eventr/native/navigation/react";
import type { PaneGroupId } from "feat/layout/screen-layout";
import { tabOfStack } from "feat/layout/tab-bar/tab-stack-ids";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { create } from "zustand";
import { isTrailingStackId, leadingStackIdOf, useSplitStore } from "./split.store";
import { ensureToolbarIcon, getToolbarIcon, TOOLBAR_ICON_SCALE } from "./toolbarIcons";
import { useHasVerticalBar } from "./vertical-bar";

/** A tab's main action, as its FAB would offer it. */
export interface IRailAction {
  id: string;
  icon: TEventrIcons;
  disabled: boolean;
  onClick: () => void;
}

interface IRailActionsStore {
  actions: Partial<Record<PaneGroupId, IRailAction>>;
  setAction: (tab: PaneGroupId, action: IRailAction | undefined) => void;
  /** Each tab's header controls, which follow its main action on the vertical bar. */
  extras: Partial<Record<PaneGroupId, IRailAction[]>>;
  setExtras: (tab: PaneGroupId, extras: IRailAction[] | undefined) => void;
}

/**
 * Each tab's main action while its split is expanded beside the vertical bar. The vertical bar
 * carries the toolbar items of the view beside it, the trailing column's, but the action belongs
 * to the leading column's screen, which publishes it here for the trailing column to show.
 */
const useRailActionsStore = create<IRailActionsStore>()((set) => ({
  actions: {},
  setAction: (tab, action) => set((s) => ({ actions: { ...s.actions, [tab]: action } })),
  extras: {},
  setExtras: (tab, extras) => set((s) => ({ extras: { ...s.extras, [tab]: extras } })),
}));

const NO_ACTIONS: IRailAction[] = [];

/** Whether a tab's main action goes on the vertical bar rather than in a FAB. */
export const useActionsOnRail = () => useHasVerticalBar();

/**
 * Which `ViewToolbarAction` last set each view's toolbar item. One that remounts sets the item
 * again before the one it replaces cleans up, so only the latest may take the item off.
 */
const toolbarOwners = new Map<string, symbol>();

/**
 * `actions` as the toolbar items of the view this renders in, top to bottom, which the vertical bar
 * shows beside the tabs. A tab root hides its navigation bar, and says so with its items;
 * `keepsBar` leaves the bar of any other screen as it is. With no actions, or once unmounted, the
 * view has no toolbar items.
 */
function ViewToolbarAction({ actions, keepsBar }: { actions: IRailAction[]; keepsBar?: boolean }) {
  const [, setIconReady] = useState(0);
  const missingIcons = actions.filter((action) => !getToolbarIcon(action.icon)).map((action) => action.icon);
  const missingKey = missingIcons.join(",");
  useEffect(() => {
    if (!missingKey) return;
    void Promise.all(missingKey.split(",").map((icon) => ensureToolbarIcon(icon))).then(() =>
      setIconReady((v) => v + 1),
    );
  }, [missingKey]);

  const ctx = useNativeNavigationViewContext({
    stackItem: {
      toolbarItems: actions.map((action) => {
        const image = getToolbarIcon(action.icon);
        return {
          id: action.id,
          title: "",
          ...(image ? { image: { uri: image, scale: TOOLBAR_ICON_SCALE } } : {}),
          enabled: !action.disabled,
        };
      }),
      ...(!keepsBar && { bar: { visible: false } }),
    },
  });

  // Claimed in a layout effect, as the item itself is set, so a replacement claims it before the
  // instance it replaces cleans up.
  const [owner] = useState(() => Symbol("ViewToolbarAction"));
  const viewId = ctx.componentId;
  useLayoutEffect(() => {
    if (viewId) toolbarOwners.set(viewId, owner);
  }, [viewId, owner]);

  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  useEffect(() => {
    const remove = ctx.addClickListener(({ buttonId }) => {
      actionsRef.current.find((action) => action.id === buttonId)?.onClick();
    });
    return () => {
      remove();
      if (!viewId || toolbarOwners.get(viewId) !== owner) return;
      toolbarOwners.delete(viewId);
      ctx.updateView({ stackItem: { toolbarItems: [], ...(!keepsBar && { bar: { visible: false } }) } }).catch(() => {
        // The view has gone, and its toolbar item with it.
      });
    };
  }, [ctx, viewId, owner, keepsBar]);

  return null;
}

/**
 * Puts `tab`'s action on the vertical bar for as long as this is mounted; `"view"` puts it on
 * this screen's own view. A tab's comes from the leading column's screen: on its own view while the split is collapsed, when that view is the one beside
 * the bar, and through the trailing column's root (see `TrailingRailAction`) while it is expanded.
 */
export function RailAction({ tab, ...action }: IRailAction & { tab: PaneGroupId | "view" }) {
  // A screen's own action is always on its own view, as on a collapsed split.
  const collapsed = useSplitStore((s) => s.collapsed) || tab === "view";
  const { id, icon, disabled, onClick } = action;
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;
  const [published] = useState(() => ({ onClick: () => onClickRef.current() }));
  useEffect(() => {
    if (collapsed) return;
    const { setAction } = useRailActionsStore.getState();
    setAction(tab, { id, icon, disabled, onClick: published.onClick });
    return () => setAction(tab, undefined);
  }, [collapsed, tab, id, icon, disabled, published]);

  // A screen that is no tab's root, such as a sheet, has its own bar and its own place beside
  // the vertical bar.
  return collapsed ? (
    <ViewToolbarAction actions={[{ id, icon, disabled, onClick: published.onClick }]} keepsBar={tab === "view"} />
  ) : null;
}

/**
 * Puts `actions`, a tab's header controls, on the vertical bar after its main action, for as long
 * as this is mounted and there is a vertical bar. Each action's `onClick` may change every render.
 * A tab with header controls has no main action on the bar, so on a collapsed split, where the
 * bar carries this view's own items, they are the view's only ones.
 */
export function RailExtras({ tab, actions }: { tab: PaneGroupId; actions: IRailAction[] }) {
  const onRail = useActionsOnRail();
  const collapsed = useSplitStore((s) => s.collapsed);
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  // Republished only when what the bar draws changes, not for every new click handler.
  const signature = actions.map((action) => `${action.id}:${action.icon}:${action.disabled}`).join("|");
  useEffect(() => {
    if (!onRail || collapsed || !signature) return;
    const published = actionsRef.current.map((action) => ({
      ...action,
      onClick: () => actionsRef.current.find((current) => current.id === action.id)?.onClick(),
    }));
    const { setExtras } = useRailActionsStore.getState();
    setExtras(tab, published);
    return () => setExtras(tab, undefined);
  }, [onRail, collapsed, tab, signature]);
  return onRail && collapsed ? <ViewToolbarAction actions={actions} /> : null;
}

/**
 * In a trailing column's screen: `tab`'s action and header controls, while its split is expanded
 * beside the vertical bar. `keepsBar` as for `ViewToolbarAction`.
 */
export function TrailingRailAction({ tab, keepsBar }: { tab: PaneGroupId; keepsBar?: boolean }) {
  const action = useRailActionsStore((s) => s.actions[tab]);
  const extras = useRailActionsStore((s) => s.extras[tab]) ?? NO_ACTIONS;
  const onRail = useActionsOnRail();
  const collapsed = useSplitStore((s) => s.collapsed);
  const show = onRail && !collapsed;
  return (
    <ViewToolbarAction actions={show ? [...(action ? [action] : []), ...extras] : NO_ACTIONS} keepsBar={keepsBar} />
  );
}

/** The tabs whose leading column publishes an action or header controls for the vertical bar. */
const RAIL_TABS: readonly PaneGroupId[] = ["home", "chat", "team"];

/**
 * In a screen pushed onto a trailing column, past its root: the tab's action and header controls,
 * as on the root, with the screen's own bar left as it is.
 */
export function TrailingColumnRailAction() {
  const { stack } = useNativeNavigationViewContext();
  const tab = isTrailingStackId(stack) ? tabOfStack(leadingStackIdOf(stack)) : undefined;
  if (!tab || !RAIL_TABS.includes(tab)) return null;
  return <TrailingRailAction tab={tab} keepsBar />;
}
